import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import { KeyRound, Loader2 } from 'lucide-react';
import { ApiError } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth, useChangePassword } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';

const MIN_LENGTH = 12;

type Strength = 'weak' | 'fair' | 'good' | 'strong';

interface StrengthResult {
  score: number; // 0-4 like zxcvbn
  label: Strength;
  message: string;
}

/**
 * Tiny placeholder strength scorer. v1 policy is "length >= 12" per the
 * scope instructions. We still bucket into 4 levels so the UI can show
 * a familiar 0–4 bar. Subagent D can swap this for real `zxcvbn` later.
 */
function scorePassword(pwd: string): StrengthResult {
  if (!pwd) return { score: 0, label: 'weak', message: 'Enter a password' };
  if (pwd.length < MIN_LENGTH) {
    return {
      score: 1,
      label: 'weak',
      message: `Too short — at least ${MIN_LENGTH} characters`,
    };
  }
  const hasLower = /[a-z]/.test(pwd);
  const hasUpper = /[A-Z]/.test(pwd);
  const hasDigit = /\d/.test(pwd);
  const hasSymbol = /[^A-Za-z0-9]/.test(pwd);
  const classes = [hasLower, hasUpper, hasDigit, hasSymbol].filter(Boolean).length;
  if (classes <= 1) {
    return { score: 2, label: 'fair', message: 'Add more character types' };
  }
  if (classes === 2 || pwd.length < 16) {
    return { score: 3, label: 'good', message: 'Looks good' };
  }
  return { score: 4, label: 'strong', message: 'Strong password' };
}

/**
 * FirstLogin — §4.1: the bootstrap password path forces a change before
 * anything else. If the current user does NOT need a password change, we
 * bounce straight to /.
 */
export default function FirstLogin() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [touched, setTouched] = useState(false);
  const change = useChangePassword();
  const { user, mustChangePassword, isLoading } = useAuth();

  useEffect(() => {
    if (change.isError) change.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, next, confirm]);

  const strength = useMemo(() => scorePassword(next), [next]);
  const mismatch = touched && confirm.length > 0 && confirm !== next;
  const canSubmit =
    current.length > 0 &&
    next.length >= MIN_LENGTH &&
    confirm === next &&
    !change.isPending;

  if (isLoading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!mustChangePassword) return <Navigate to="/" replace />;

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!canSubmit) return;
    change.mutate({ old_password: current, new_password: next });
  };

  const errorMessage =
    change.error instanceof ApiError
      ? change.error.detail ?? change.error.title
      : change.error?.message;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-[420px]">
        <CardHeader className="items-center space-y-3 text-center">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <KeyRound className="h-5 w-5" aria-hidden />
          </div>
          <CardTitle>Set your password</CardTitle>
          <p className="text-sm text-muted-foreground">
            You're signed in with the bootstrap password. Choose a new one
            before continuing.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div className="space-y-1.5">
              <Label htmlFor="current">Current password</Label>
              <Input
                id="current"
                type="password"
                autoComplete="current-password"
                required
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="new">New password</Label>
              <Input
                id="new"
                type="password"
                autoComplete="new-password"
                required
                value={next}
                onChange={(e) => {
                  setNext(e.target.value);
                  setTouched(true);
                }}
              />
              <StrengthMeter strength={strength} />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="confirm">Confirm password</Label>
              <Input
                id="confirm"
                type="password"
                autoComplete="new-password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                aria-invalid={mismatch}
              />
              {mismatch ? (
                <p className="text-xs text-destructive">
                  Passwords do not match
                </p>
              ) : null}
            </div>

            {errorMessage ? (
              <div
                role="alert"
                className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
              >
                {errorMessage}
              </div>
            ) : null}

            <Button type="submit" className="w-full" disabled={!canSubmit}>
              {change.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  Saving…
                </>
              ) : (
                'Update password'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function StrengthMeter({ strength }: { strength: StrengthResult }) {
  return (
    <div className="space-y-1">
      <div className="flex gap-1" aria-hidden>
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className={cn(
              'h-1 flex-1 rounded-full bg-muted transition-colors',
              strength.score >= i && strengthColor(strength.label),
            )}
          />
        ))}
      </div>
      <p
        className={cn(
          'text-[11px]',
          strength.score >= 3 ? 'text-success' : 'text-muted-foreground',
        )}
      >
        {strength.message}
      </p>
    </div>
  );
}

function strengthColor(label: Strength): string {
  switch (label) {
    case 'weak':
      return 'bg-destructive';
    case 'fair':
      return 'bg-warning';
    case 'good':
    case 'strong':
      return 'bg-success';
  }
}
